import { defaultSearchItems } from './defaultSearchItems';
import { useObjectListUniversalSearch } from '../../object-list/useObjectListUniversalSearch';
import { QuickSearchLightboxItem } from '../../../common/quick-search-lightbox/types';
import { useCallback, useMemo } from 'react';
import { sortSearchResults } from '../sortSearchResults';
import {
  configurationTypeToIcon,
  DocumentationCardContentViewType,
} from '../../salesforce-documentation-card/types';
import { Connect } from '@sweep-io/sweep-design/dist/icons';
import { UniversalSearchListItem } from '../UniversalSearchListItem';
import { SearchType } from '../types';
import { SalesforceItem } from '../../../parser/ParserTypes';
import { SearchLightbox } from '../../building-blocks/search/SearchLightbox';
import { SearchFilters } from '../../building-blocks/search/SearchFilters';

interface DocumentationLightboxProps {
  onClose: () => void;
  crmOrgId: string;
}

export const DocumentationLightbox = ({ onClose, crmOrgId }: DocumentationLightboxProps) => {
  const {
    searchTxt,
    tempSearchTxt,
    onSearchKeydownWithEscHandler,
    onSearchChange,
    onClearButtonClick,
    onSelectedFilterItem,
    results,
    isLoadingResults,
    onListItemClick,
    searchTypeOptions,
    selectedSearchType,
    onSelectSearchTypeOption,
    handleCloseUniversalSearch,
    openFullModeUniversalSearch,
    performSearchProgrammatically,
    filterOptions,
    selectedFilterValue,
  } = useObjectListUniversalSearch({
    crmOrgId,
  });

  const _onSelectSearchTypeOption = useCallback(
    async (selectedValue: string) => {
      await onSelectSearchTypeOption(selectedValue);

      if (selectedValue === SearchType.content) {
        openFullModeUniversalSearch();
        onClose();
      }
    },
    [onSelectSearchTypeOption, openFullModeUniversalSearch, onClose],
  );

  const translatedResults: QuickSearchLightboxItem<SalesforceItem>[] = useMemo(() => {
    if (!results) return [];

    const list = results
      ? Object.values(sortSearchResults(results, crmOrgId, searchTxt)).flat()
      : undefined;

    return list
      ? list.map((item) => ({
          id: item.id,
          title: item.name,
          subtitle: item.label,
          icon: configurationTypeToIcon[item.parentType] ?? <Connect />,
          data: {
            ...item,
            contentType:
              item.match !== undefined
                ? DocumentationCardContentViewType.sourceCode
                : DocumentationCardContentViewType.default,
          },
        }))
      : [];
  }, [crmOrgId, results, searchTxt]);

  const onItemClick = useCallback(
    (item: QuickSearchLightboxItem<SalesforceItem> | SalesforceItem) => {
      if (!('data' in item) && 'title' in item) {
        performSearchProgrammatically(item.title);
        return;
      }

      const searchListItem = 'data' in item;
      if (searchListItem) {
        openFullModeUniversalSearch();
        onListItemClick({
          item: item.data!,
          contentType:
            item.data!.match !== undefined
              ? DocumentationCardContentViewType.sourceCode
              : DocumentationCardContentViewType.default,
          clearHistory: true,
        });
        onClose();
      }
    },
    [onListItemClick, performSearchProgrammatically, onClose, openFullModeUniversalSearch],
  );

  const renderListElement = (item: SalesforceItem) => {
    if (!item) return null;
    return (
      <UniversalSearchListItem
        key={item.id}
        item={item}
        onListItemClick={() =>
          onItemClick({
            id: item.id,
            title: item.name,
            subtitle: item.label,
            icon: configurationTypeToIcon[item.parentType] ?? <Connect />,
            data: item,
          })
        }
        isExpandedMode={false}
        forceRenderIcons={true}
        searchTxt={''}
      />
    );
  };

  return (
    <SearchLightbox
      isLoading={isLoadingResults}
      searchTxt={searchTxt}
      tempSearchTxt={tempSearchTxt}
      onSearchChange={onSearchChange}
      onKeyDown={onSearchKeydownWithEscHandler}
      onClearButtonClick={onClearButtonClick}
      translatedResults={translatedResults}
      defaultItems={defaultSearchItems}
      onItemClick={onItemClick}
      onShowAllClick={() => {
        openFullModeUniversalSearch();
        onClose();
      }}
      onClose={() => {
        handleCloseUniversalSearch();
        onClose();
      }}
      renderListElement={renderListElement}
      filterComponent={
        <SearchFilters
          filterOptions={filterOptions}
          selectedFilterValue={selectedFilterValue ?? ''}
          onSelectedFilterItem={onSelectedFilterItem}
          menuOptions={searchTypeOptions}
          selectedMenuOption={selectedSearchType}
          onSelectMenuOption={_onSelectSearchTypeOption}
        />
      }
    />
  );
};
